import { useTranslation } from 'react-i18next';

import { gradientCss } from '@/lib/colormaps';
import {
  CUT_LEGEND_KEY,
  CUT_LEGEND_SHORT_KEY,
  FILL_LEGEND_KEY,
  FILL_LEGEND_SHORT_KEY,
  cutEnd,
  type SignedSurfaceKind,
} from '@/lib/cutFillConvention';

/** An extra tick on the bar, in the SAME units as `min`/`max` (metres). */
export interface ColorbarMark {
  value: number;
  label: string;
}

interface Props {
  layerName: string;
  min: number;
  max: number;
  gradientName?: string;
  unit?: string;
  /** What the scalar IS. Defaults to the deviation legend — a clearance run
   *  shades by the `Clearance` scalar and passes its own label instead. */
  label?: string;
  /** Replaces the bare "0" tick when the scalar has a NAMED boundary. A
   *  clearance run passes "profile": zero is not an origin there, it is the
   *  envelope, and saying so is the difference between a legend and a ruler. */
  zeroLabel?: string;
  /** Extra ticks, e.g. the near-miss ladder. Their labels get their OWN row
   *  under the range labels, so they never overprint them; a tick that would
   *  merge into the zero tick, sit on top of a range label at either end, or
   *  collide with another mark's label is dropped. */
  marks?: ColorbarMark[];
  /** The data goes PAST `max` (values beyond it saturate at the last colour).
   *  Renders the top label as `≥+…`. A clearance run passes this when
   *  `summary.maxIntrusionM > rampSpanM`, which ruling P9 makes routine: the
   *  span is P98 of the intrusions, so the worst 2 % are off the end of the
   *  ramp. Without it the bar says "+0.55" while the chip beneath it says
   *  "worst 2.00" and neither admits the other exists. */
  maxCapped?: boolean;
  /**
   * Which signed quantity the bar describes, and therefore WHICH END is cut
   * (T4, closing F21). Defaults to `'deviation'`, matching `label`'s own
   * default — a caller that passes neither is a deviation cloud.
   *
   * `null` suppresses the cut/fill words for a scalar that has no cut side.
   * A clearance bar does not need to pass it: a bar with a NAMED zero
   * (`zeroLabel`) is by definition not measuring material against a design,
   * so the words are withheld there too.
   */
  kind?: SignedSurfaceKind | null;
}

/**
 * A mark whose TICK LINE would merge into the zero tick is dropped — two
 * meanings on one hairline.
 *
 * Deliberately tiny (finding M2). The old 0.06 was sized for a mark LABEL
 * colliding with the zero label, and the marks have had a row of their own
 * since: nothing but other marks lives there, so the only real collision left
 * is between the 1 px rules on the bar itself. At 0.06 the inner 0.1 m tick
 * silently disappeared above a ~0.83 m span and both ticks above ~2.9 m — the
 * ladder stopped being labelled exactly on the runs with the deepest
 * intrusions. 0.012 is ~2 px on a `min-w-48` bar, which is where two hairlines
 * genuinely become one.
 */
const MARK_ZERO_GAP = 0.012;
/** Minimum gap between two mark labels on the marks row, ~35 px on a
 *  `min-w-48` bar — below it the two numbers touch. */
const MARK_LABEL_GAP = 0.12;
/**
 * A mark this close to an END of the bar is dropped (finding M1): the range
 * labels are anchored hard to `left-0` / `right-0` and the outermost marks
 * anchor to the same edge, so the two print on top of each other — and on the
 * common floored clearance run (`rampSpanM` at its 0.35 m floor) they print the
 * SAME NUMBER twice, once as the range end and once as the outer near-miss
 * band. Only the outer 1.5 % of the bar is affected, so every tick that says
 * something the range labels do not still renders.
 */
const MARK_EDGE_GAP = 0.015;

/**
 * Units, scale and decimals for a scalar bar. Exported because callers that
 * supply `marks` have to label them in whatever unit the bar resolved to —
 * "35 cm" under a metres axis (or vice versa) is a bug, and the only way to
 * be sure is to ask the bar.
 *
 * Survey deviations are typically sub-metre; showing "+0.050 m" wastes
 * precision and reads worse than "+5 cm". Centimetres when the largest
 * absolute end is < 0.5 m, millimetres when < 0.005 m. Decimals keep ~2
 * significant figures at the larger end: ±5 cm → "5.00", ±50 mm → "50".
 */
export function scalarBarFormat(min: number, max: number, unit?: string) {
  const absMax = Math.max(Math.abs(min), Math.abs(max), 1e-9);
  let scale = 1;
  let resolvedUnit = unit ?? 'm';
  if (unit === undefined) {
    if (absMax < 0.005) {
      scale = 1000;
      resolvedUnit = 'mm';
    } else if (absMax < 0.5) {
      scale = 100;
      resolvedUnit = 'cm';
    }
  }
  const decimals = Math.max(0, 2 - Math.floor(Math.log10(Math.max(absMax * scale, 1e-9))));
  return { scale, unit: resolvedUnit, format: (v: number) => (v * scale).toFixed(decimals) };
}

export function DeviationColorbar({
  layerName,
  min,
  max,
  gradientName = 'SPECTRAL',
  unit,
  label,
  zeroLabel,
  marks,
  maxCapped = false,
  kind = 'deviation',
}: Props) {
  const { t } = useTranslation('project');
  // The bar's AXIS always runs min -> max, left to right. The convention puts
  // cut at the palette's HIGH end, and for a surface difference the cut end is
  // `min` — so the palette itself has to be drawn backwards, or the words
  // below would sit on the correct end of a ramp running the wrong way.
  const gradient = gradientCss(gradientName, kind != null && cutEnd(kind) === 'min');

  const { unit: resolvedUnit, format: fmt } = scalarBarFormat(min, max, unit);

  // Visual position of the "0" tick on the colorbar. The shader maps values
  // linearly across [min, max], so 0 is NOT at the visual centre when the
  // range is asymmetric — it sits at (0 − min) / (max − min) of the way.
  // Mis-rendering this caused real confusion: with min=-10, max=+1, a
  // 0.01 m point lands at 91 % of the gradient (very red), not near the
  // visual centre as the old "left/centre/right" legend implied.
  const zeroFracRaw = max > min ? (0 - min) / (max - min) : 0.5;
  const zeroFrac = Math.min(0.96, Math.max(0.04, zeroFracRaw));
  const zeroInRange = min < 0 && max > 0;

  // Extra ticks. Their labels live on their OWN row, because the near-miss
  // ladder is always CLOSE to the boundary by definition: the inner 0.1 m band
  // sits at 0.1/(2·rampSpanM) from the zero tick, i.e. within 14 % of centre
  // for every span at or above the 0.35 m floor. Sharing the range-label row
  // meant that mark either overprinted "profile" or was thrown away — it never
  // rendered cleanly. On its own row it only has to clear OTHER marks.
  //
  // Three guards survive, and all three are about what shares a LINE with the
  // mark rather than about the ladder itself: a mark on the outer 1.5 % of the
  // bar is dropped because the range labels are anchored there (M1 — on a
  // floored run it printed the same number twice); a mark whose 1 px tick would
  // merge into the zero tick is dropped (M2 — at the tick's own width, not at a
  // label's); and of two marks closer than a label width apart the outer one
  // wins, because it is the one that says where the ladder starts.
  const placedMarks: { frac: number; label: string; key: string }[] = [];
  for (const { value, label: markLabel } of marks ?? []) {
    if (!(max > min)) break;
    const frac = (value - min) / (max - min);
    if (frac < 0 || frac > 1) continue;
    if (frac < MARK_EDGE_GAP || frac > 1 - MARK_EDGE_GAP) continue;
    if (zeroInRange && Math.abs(frac - zeroFrac) < MARK_ZERO_GAP) continue;
    const previous = placedMarks[placedMarks.length - 1];
    if (previous && Math.abs(frac - previous.frac) < MARK_LABEL_GAP) continue;
    placedMarks.push({ frac, label: markLabel, key: `${value}` });
  }
  // Which end of the bar the two words belong at. `cutEnd` is derived from
  // the same `signIsCut` every colour surface uses, so the words and the ink
  // cannot end up on opposite ends.
  const CUT_WORDS = { short: CUT_LEGEND_SHORT_KEY, full: CUT_LEGEND_KEY };
  const FILL_WORDS = { short: FILL_LEGEND_SHORT_KEY, full: FILL_LEGEND_KEY };
  const cutAtMax = kind != null && cutEnd(kind) === 'max';
  const lowEnd = cutAtMax ? FILL_WORDS : CUT_WORDS;
  const highEnd = cutAtMax ? CUT_WORDS : FILL_WORDS;
  const showCutFill = kind != null && zeroLabel === undefined && zeroInRange;

  // A label centred on a tick at 0 % or 100 % would hang half outside the
  // card, so the outermost ones anchor to their edge instead.
  const markAnchor = (frac: number) =>
    frac < MARK_LABEL_GAP ? 'translate-x-0' : frac > 1 - MARK_LABEL_GAP ? '-translate-x-full' : '-translate-x-1/2';

  return (
    <div className='rounded-lg shadow-lg px-3 py-2 backdrop-blur bg-white/90 dark:bg-stone-900/90 min-w-48'>
      <div className='text-xs text-slate-700 dark:text-stone-200 font-medium truncate mb-1' title={layerName}>
        {layerName}
      </div>
      <div className='text-[10px] text-slate-500 dark:text-stone-400 mb-1'>
        {label ?? t('analyses.deviationLegend', { defaultValue: 'Deviation' })} ({resolvedUnit})
      </div>
      <div className='relative w-full h-3 rounded' style={{ background: gradient }}>
        {placedMarks.map((m) => (
          <div
            key={m.key}
            className='absolute top-0 h-full w-px bg-slate-900/35 dark:bg-white/35'
            style={{ left: `${m.frac * 100}%` }}
            aria-hidden
          />
        ))}
        {zeroInRange && (
          <div
            className='absolute top-0 h-full w-px bg-slate-900/70 dark:bg-white/70'
            style={{ left: `${zeroFrac * 100}%` }}
            aria-hidden
          />
        )}
      </div>
      <div className='relative h-4 mt-1 text-[11px] text-slate-700 dark:text-stone-300'>
        <span className='absolute left-0'>{fmt(min)}</span>
        {zeroInRange && (
          <span className='absolute -translate-x-1/2' style={{ left: `${zeroFrac * 100}%` }}>
            {zeroLabel ?? '0'}
          </span>
        )}
        {/* `≥` when the data runs past the range: everything beyond `max` is
            drawn in the last colour, and the number alone would deny it. */}
        <span className='absolute right-0'>
          {maxCapped ? '≥' : ''}+{fmt(max)}
        </span>
      </div>
      {/* Cut = red, said in words (T4). The colour cannot carry it on its own:
          the ramp is the user's to change, and a red/green-deficient reader
          gets nothing from a diverging ramp at all. The words sit on the end
          the CONVENTION calls cut — the positive end for a deviation, the
          negative end for a surface difference (`dz = top − bottom`) — so a
          reader holding the two side by side reads the same word for the same
          material. Withheld when zero is off the bar (one side is not two)
          and when the scalar has a named boundary instead of an origin. */}
      {showCutFill && (
        <div
          className='mt-0.5 flex items-baseline justify-between gap-2 text-[10px] leading-tight text-slate-500 dark:text-stone-400'
          data-testid='colorbar-cutfill'>
          <span className='text-left' title={t(lowEnd.full)}>
            {t(lowEnd.short)}
          </span>
          <span className='text-right' title={t(highEnd.full)}>
            {t(highEnd.short)}
          </span>
        </div>
      )}
      {placedMarks.length > 0 && (
        <div className='relative h-3.5 text-[10px] text-slate-500 dark:text-stone-400'>
          {placedMarks.map((m) => (
            <span key={m.key} className={`absolute ${markAnchor(m.frac)}`} style={{ left: `${m.frac * 100}%` }}>
              {m.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
